import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { VendorProfile } from '@trnsact/trnsact-shared-types';
import { DocumentContext } from 'contexts/DocumentContext';
import { documentsByKey } from 'constants/common';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { Q_DOCUMENTS_BY_VOID } from '../api/getDocumentsByVOId';
import { DocumentRow } from '../types';
import { formatDocumentsToTableData, filterDocumentsBySearchTerm } from '../lib';
import { QUERY_PORTAL_CONFIGURATION } from 'queries/common';
import { LenderProfile } from 'types';

interface Options {
  vo: VendorOpportunity;
  vp: VendorProfile;
  lender: LenderProfile;
  onSharedDocumentsChange: (documents: Array<{ documentId: string; lenderAccess: boolean }>) => void;
}

export function useDocumentsAccessData({ vo, vp, lender, onSharedDocumentsChange }: Options) {
  const documentContext = useContext(DocumentContext);

  const [documentAccessOverrides, setDocumentAccessOverrides] = useState<Record<string, boolean>>({});
  const [searchValue, setSearchValue] = useState('');

  const { data: portalConfigurationData } = useQuery(QUERY_PORTAL_CONFIGURATION, {
    variables: { vendorProfileId: (vp as any).id, types: ['DOCUMENTS'] },
    fetchPolicy: 'no-cache',
  });

  const {
    data: userDocsData,
    refetch: refetchDocs,
    loading: isLoading,
  } = useQuery(Q_DOCUMENTS_BY_VOID, {
    variables: { VOId: vo.vendorOpportunityId, docId: null },
  });

  useEffect(() => {
    if (refetchDocs) refetchDocs();
  }, [documentContext.lastDoc, refetchDocs]);

  const defaultAllowedDocuments = useMemo(() => {
    if (!userDocsData?.documents) return [];

    const portalConfiguration = portalConfigurationData?.portalConfigurations?.[0] as any;
    const docSettings =
      portalConfiguration?.jsonDefinition?.config?.documentsByTypeSettings ?? ({} as Record<string, any>);
    const hiddenKeys = Object.keys(docSettings).filter(key => docSettings[key]?.hiddenForLenders);

    const targetByText: string[] = hiddenKeys.map(
      (e: string) => documentsByKey[e as keyof typeof documentsByKey]?.text ?? ''
    );
    const targetByValue: string[] = hiddenKeys.map(
      (e: string) => documentsByKey[e as keyof typeof documentsByKey]?.value ?? ''
    );

    return userDocsData.documents
      .filter((doc: any) => {
        const lenderAccess = doc.documentCreditSubmission?.lenderAccess;

        if (typeof lenderAccess === 'boolean') {
          return lenderAccess;
        }

        return !targetByText.includes(doc.docType) && !targetByValue.includes(doc.docType);
      })
      .map((doc: any) => doc.documentId);
  }, [portalConfigurationData, userDocsData]);

  const allowedDocuments = useMemo(() => {
    if (!userDocsData?.documents) return [];

    const defaultAllowedDocumentIds = new Set(defaultAllowedDocuments);

    return userDocsData.documents
      .filter((doc: any) => documentAccessOverrides[doc.documentId] ?? defaultAllowedDocumentIds.has(doc.documentId))
      .map((doc: any) => doc.documentId);
  }, [defaultAllowedDocuments, documentAccessOverrides, userDocsData]);

  // Notify parent whenever allowedDocuments changes
  useEffect(() => {
    if (!userDocsData?.documents) return;

    const documents = userDocsData.documents.map((doc: any) => ({
      documentId: doc.documentId,
      lenderAccess: allowedDocuments.includes(doc.documentId),
    }));

    onSharedDocumentsChange(documents);
  }, [allowedDocuments, onSharedDocumentsChange, userDocsData]);

  const handleToggle = useCallback(
    (documentId: string) => {
      setDocumentAccessOverrides(prev => {
        const defaultValue = defaultAllowedDocuments.includes(documentId);
        const currentValue = prev[documentId] ?? defaultValue;

        return {
          ...prev,
          [documentId]: !currentValue,
        };
      });
    },
    [defaultAllowedDocuments]
  );

  const tableData: DocumentRow[] = useMemo(() => {
    if (!userDocsData?.documents) return [];

    const docs = formatDocumentsToTableData(userDocsData.documents);

    if (lender?.submissionMethod === 'DTFS') {
      docs.push({
        id: 'dtfs-credit-app-doc-id',
        documentId: 'dtfs-credit-app-doc-id',
        docName: 'Credit Application Snapshot',
        createdBy: 'Trnsact',
        createdDateTime: new Date().toISOString(),
        docType: 'Snapshot',
        docDescription: 'Credit Application',
        lenderAccess: '',
      });
    }

    return filterDocumentsBySearchTerm(docs, searchValue);
  }, [userDocsData, searchValue, lender]);

  const allowedCount = allowedDocuments.length;
  const totalCount = userDocsData?.documents?.length ?? 0;

  return {
    isLoading,
    tableData,
    searchValue,
    allowedCount,
    totalCount,
    allowedDocuments,
    setSearchValue,
    handleToggle,
  };
}
